/**
 * Pieces the user acts on: button, text field, choice list, checkbox. Field values live in the
 * window ({@link UiContext}); a button sends them all with its action.
 */
import { For, Show, type JSX } from "solid-js";
import type { UiOption } from "@protocol/UiOption";
import type { UiContext } from "./context";

/** A button: sends `action` to the plugin. */
export function UiButton(props: { label: string; action: string; primary: boolean; ctx: UiContext }): JSX.Element {
  return (
    <button type="button" class="btn" classList={{ primary: props.primary }} data-ui="button" data-ui-action={props.action} disabled={props.ctx.busy()} onClick={() => props.ctx.press(props.action)}>
      {props.label}
    </button>
  );
}

/** A label above a field, when there is one. */
function Labelled(props: { label: string; children: JSX.Element }): JSX.Element {
  return (
    <label class="ui-field">
      <Show when={props.label}>
        <span class="small muted">{props.label}</span>
      </Show>
      {props.children}
    </label>
  );
}

/** A one-line text field. */
export function UiInput(props: { name: string; label: string; placeholder: string; ctx: UiContext }): JSX.Element {
  return (
    <Labelled label={props.label}>
      <input type="text" data-ui="input" data-ui-field={props.name} placeholder={props.placeholder} value={props.ctx.value(props.name)} onInput={(e) => props.ctx.setValue(props.name, e.currentTarget.value)} />
    </Labelled>
  );
}

/** A choice among options. */
export function UiSelect(props: { name: string; label: string; options: UiOption[]; ctx: UiContext }): JSX.Element {
  return (
    <Labelled label={props.label}>
      <select data-ui="select" data-ui-field={props.name} value={props.ctx.value(props.name)} onChange={(e) => props.ctx.setValue(props.name, e.currentTarget.value)}>
        <For each={props.options}>{(o) => <option value={o.value}>{o.label || o.value}</option>}</For>
      </select>
    </Labelled>
  );
}

/** A yes/no box. */
export function UiCheckbox(props: { name: string; label: string; ctx: UiContext }): JSX.Element {
  return (
    <label class="ui-check small">
      <input type="checkbox" data-ui="checkbox" data-ui-field={props.name} checked={props.ctx.value(props.name) === "true"} onChange={(e) => props.ctx.setValue(props.name, String(e.currentTarget.checked))} />
      {props.label}
    </label>
  );
}
